import { useEffect, useState } from 'react'
import api from '@/services/api'

/**
 * Selling currency for the vendor's storefront and checkout.
 *
 * Reads and writes `settings.currency`. Bokun prices, the storefront, the
 * Stripe hold and the Bokun confirmation all follow this value, so a EUR
 * vendor is quoted and charged in EUR rather than Bokun's USD conversion.
 * Products are re-priced on the next catalog sync (every 4 hours), which
 * re-fetches any product stored in a different currency.
 *
 * Used on the vendor Settings page and the admin vendor detail page.
 */
interface CurrencySectionProps {
  vendorId?: string
}

const DEFAULT_CURRENCY = 'USD'
const EXAMPLE_AMOUNT = 25

const CURRENCIES: { code: string; name: string }[] = [
  { code: 'USD', name: 'US dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British pound' },
  { code: 'CHF', name: 'Swiss franc' },
  { code: 'CAD', name: 'Canadian dollar' },
  { code: 'AUD', name: 'Australian dollar' },
  { code: 'NZD', name: 'New Zealand dollar' },
  { code: 'JPY', name: 'Japanese yen' },
  { code: 'SGD', name: 'Singapore dollar' },
  { code: 'HKD', name: 'Hong Kong dollar' },
  { code: 'MXN', name: 'Mexican peso' },
  { code: 'BRL', name: 'Brazilian real' },
  { code: 'SEK', name: 'Swedish krona' },
  { code: 'NOK', name: 'Norwegian krone' },
  { code: 'DKK', name: 'Danish krone' },
  { code: 'PLN', name: 'Polish zloty' },
  { code: 'CZK', name: 'Czech koruna' },
  { code: 'HUF', name: 'Hungarian forint' },
  { code: 'TRY', name: 'Turkish lira' },
  { code: 'AED', name: 'UAE dirham' },
  { code: 'ZAR', name: 'South African rand' },
]

function currencyName(code: string): string {
  return CURRENCIES.find((c) => c.code === code)?.name ?? code
}

/** How an example price renders in this currency, e.g. "€25.00". */
function examplePrice(code: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format(EXAMPLE_AMOUNT)
  } catch {
    return `${code} ${EXAMPLE_AMOUNT.toFixed(2)}`
  }
}

export default function CurrencySection({ vendorId }: CurrencySectionProps) {
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY)
  const [savedCurrency, setSavedCurrency] = useState<string>(DEFAULT_CURRENCY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!vendorId) return
    api.vendors
      .get(vendorId)
      .then((res) => {
        const data = res.data?.data ?? res.data
        const current = String(data?.settings?.currency || DEFAULT_CURRENCY).toUpperCase()
        setCurrency(current)
        setSavedCurrency(current)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [vendorId])

  const save = async () => {
    if (!vendorId || currency === savedCurrency) return
    setError(null)
    setSuccess(null)
    setSaving(true)
    try {
      await api.vendors.update(vendorId, { settings: { currency } })
      setSavedCurrency(currency)
      setSuccess(`Saved. Checkout now charges in ${currency}; listed prices update within 4 hours.`)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not save currency. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!vendorId) return null

  // Keep an unlisted saved value selectable rather than silently showing USD.
  const options = CURRENCIES.some((c) => c.code === savedCurrency)
    ? CURRENCIES
    : [{ code: savedCurrency, name: savedCurrency }, ...CURRENCIES]
  const changed = currency !== savedCurrency

  return (
    <div className="card">
      <h3 style={{ marginBottom: '0.5rem' }}>Selling currency</h3>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.25rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
        The currency your prices are shown and charged in, on your storefront and in AI bookings.
        Use the same currency your booking system (e.g. Bokun) prices in.
      </p>

      {loading ? (
        <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem' }}>Loading current currency...</p>
      ) : (
        <>
          <div style={currentRowStyle}>
            <span style={badgeStyle}>{savedCurrency}</span>
            <span style={{ fontSize: '0.9rem', color: 'var(--color-gray-700)' }}>
              {currencyName(savedCurrency)}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--color-gray-500)' }}>
              Prices show as{' '}
              <strong style={{ color: 'var(--color-gray-800)' }}>{examplePrice(savedCurrency)}</strong>
            </span>
          </div>

          <label htmlFor="vendor-currency" style={labelStyle}>
            Change currency
          </label>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <select
              id="vendor-currency"
              value={currency}
              disabled={saving}
              onChange={(e) => {
                setCurrency(e.target.value)
                setError(null)
                setSuccess(null)
              }}
              style={{ flex: '1 1 16rem', minWidth: 0, cursor: 'pointer' }}
            >
              {options.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} · {c.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={save}
              disabled={saving || !changed}
              style={{ flex: '0 0 auto', background: 'var(--color-tixxly-navy)', whiteSpace: 'nowrap' }}
            >
              {saving ? 'Saving...' : 'Save currency'}
            </button>
          </div>

          {changed && (
            <div style={warningStyle} role="status">
              <p style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>
                Customers will see and pay prices in {currencyName(currency)} ({currency})
              </p>
              <p style={{ margin: 0 }}>
                Every product is re-priced in {currency}. Checkout switches as soon as you save;
                listed prices update within 4 hours. Prices you typed into text, such as FAQ
                answers, are not converted.
              </p>
            </div>
          )}

          {error && (
            <p style={{ color: 'var(--color-error)', marginTop: '1rem', marginBottom: 0, fontSize: '0.9rem' }}>{error}</p>
          )}
          {success && (
            <p style={{ color: 'var(--color-success)', marginTop: '1rem', marginBottom: 0, fontSize: '0.9rem' }}>{success}</p>
          )}
        </>
      )}
    </div>
  )
}

const currentRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
  padding: '0.75rem 1rem',
  marginBottom: '1.25rem',
  background: 'var(--color-gray-50)',
  border: '1px solid var(--color-gray-200)',
  borderRadius: 'var(--radius-md)',
}

const badgeStyle: React.CSSProperties = {
  padding: '0.15rem 0.55rem',
  borderRadius: '999px',
  background: 'var(--color-tixxly-navy)',
  color: 'white',
  fontSize: '0.8rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const warningStyle: React.CSSProperties = {
  marginTop: '1rem',
  padding: '0.75rem 1rem',
  borderRadius: 'var(--radius-md)',
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  color: '#9a3412',
  fontSize: '0.85rem',
  lineHeight: 1.6,
}
